import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { storybooksEnabled } from '@/lib/feature-flags'
import { supabase } from '@/supabase/client'

const tabClass = ({ isActive }: { isActive: boolean }) =>
  `flex-1 rounded-xl px-4 py-2 text-center text-sm font-medium transition ${
    isActive ? 'bg-white text-cocoa shadow-soft' : 'bg-white/40 text-cocoa/70 hover:bg-white/70'
  }`

export default function TeacherDashboard() {
  const location = useLocation()
  const { user, session, profile, setOfflineMode } = useAuth()
  const storybooksEnabledFlag = storybooksEnabled()
  const bootstrapAttempted = useRef<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token = session?.access_token
    const role = profile?.role
    if (!token) return
    if (!['teacher', 'admin', 'superadmin'].includes(role ?? '')) return
    if (bootstrapAttempted.current === token) return
    bootstrapAttempted.current = token

    setError(null)

    ;(async () => {
      try {
        const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
        const response = await fetch(`${CORE_API}/api/v1/teacher/bootstrap`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          const bootstrapError = new Error(text || `Bootstrap request failed with status ${response.status}`) as Error & { status?: number }
          bootstrapError.status = response.status
          throw bootstrapError
        }

        const { error: budgetsError } = await supabase.from('teacher_budgets').select('*')
        if (budgetsError) throw budgetsError
        setOfflineMode(false)
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
        const errWithStatus = err as Error & { status?: number }
        if (errWithStatus?.status == null || errWithStatus.status >= 500) {
          setOfflineMode(true)
        }
      }
    })()
  }, [profile?.role, session?.access_token, setOfflineMode])

  const basePath = '/portal/teacher'

  const isOrderRoute = /\/order\//.test(location.pathname)
  const isStorybooksRoute = location.pathname.includes('/storybooks')
  const isAiMarketplaceRoute = location.pathname.includes('/ai-marketplace')
  const isToolsRoute = location.pathname.includes('/tools') && !isAiMarketplaceRoute
  const isBudgetRoute = location.pathname.includes('/budget')
  const isReimbRoute = location.pathname.includes('/reimbursements')
  const isSupportRoute = location.pathname.includes('/support')
  const isMarketplaceRoute = location.pathname.includes('/marketplace')
  const isPublicRoute = location.pathname.includes('/public')
  const isSettingsRoute = location.pathname.includes('/settings')
  const isOverviewRoute = location.pathname === basePath || location.pathname === `${basePath}/`

  const tabs = useMemo(
    () => [
      { key: 'overview', label: 'Overview', to: basePath, end: true },
      { key: 'budget', label: 'Budget', to: `${basePath}/budget` },
      { key: 'reimbursements', label: 'Reimbursements', to: `${basePath}/reimbursements` },
      { key: 'support', label: 'Support', to: `${basePath}/support` },
      { key: 'shopping', label: 'Shopping', to: `${basePath}/shopping` },
      { key: 'marketplace', label: 'Marketplace', to: `${basePath}/marketplace` },
      { key: 'public', label: 'Public Page', to: `${basePath}/public` },
      { key: 'tools', label: 'Teacher Tools', to: `${basePath}/tools` },
      { key: 'ai-marketplace', label: 'AI Marketplace', to: `${basePath}/ai-marketplace` },
      { key: 'settings', label: 'Settings', to: `${basePath}/settings` },
      ...(storybooksEnabledFlag ? [{ key: 'storybooks', label: 'Storybooks', to: `${basePath}/storybooks` }] : []),
      { key: 'corner', label: 'Teachers Corner', to: `${basePath}/corner` },
    ],
    [basePath, storybooksEnabledFlag]
  )

  const tabGridClass =
    tabs.length >= 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : tabs.length >= 3
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : 'grid-cols-1 sm:grid-cols-2'

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <p className="text-sm uppercase tracking-wide text-cocoa/60">Teacher Portal</p>
        <h1 className="text-3xl font-semibold text-cocoa">Welcome{user?.email ? `, ${user.email}` : ''}</h1>
        <p className="text-cocoa/70">
          Manage your one-time cards, review active shopping sessions, and check order statuses in one place.
        </p>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Budgets update automatically after approvals and NUMA now replies to support tickets with instant status checks.
        </div>
        {error && <div className="alert alert-warning">Bootstrap error: {error}</div>}
      </header>

      <nav className={`grid ${tabGridClass} gap-2 rounded-2xl bg-shell p-1`}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.key}
            to={tab.to}
            end={tab.end}
            className={(args) => {
              const active =
                args.isActive ||
                (tab.key === 'budget' && isBudgetRoute) ||
                (tab.key === 'reimbursements' && isReimbRoute) ||
                (tab.key === 'support' && isSupportRoute) ||
                (tab.key === 'shopping' && isOrderRoute) ||
                (tab.key === 'marketplace' && isMarketplaceRoute) ||
                (tab.key === 'public' && isPublicRoute) ||
                (tab.key === 'storybooks' && isStorybooksRoute) ||
                (tab.key === 'tools' && isToolsRoute) ||
                (tab.key === 'ai-marketplace' && isAiMarketplaceRoute) ||
                (tab.key === 'settings' && isSettingsRoute)
              return tabClass({ isActive: active })
            }}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <section
        className={`rounded-3xl shadow-soft ${
          isOverviewRoute ? 'overflow-hidden bg-transparent p-0' : 'bg-white p-0'
        }`}
      >
        <div className={isOverviewRoute ? '' : 'p-6'}>
          <Outlet />
        </div>
      </section>
    </div>
  )
}
